// Comandos do agregado Devolução — registrar a Devolução de um Lote (as quatro
// colunas por Tipo de Peça + baixa automática e FIFO do Backlog, ADR-0019),
// que encerra o Lote. Dado puro; o executor só chama a função de domínio que
// já existe.
//
// Vale local (o Store roda na hora) e hospedado (o servidor roda o mesmo
// executor) — mesmo código.

import type { DataISO } from "../domain/datas.ts";
import type { Snapshot } from "../domain/snapshot.ts";
import { registrarDevolucao, registrarDevolucoesEmMassa } from "../domain/devolucao.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import { comoObjeto, exigirLista, exigirTexto, lista, paraNumero, texto } from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

/** Uma quantidade retida por Motivo de Retenção, como a tela de Devolução coleta. */
export interface RetidaComando {
  motivo: string;
  quantidade: number;
}

export interface LinhaDevolucaoComando {
  tipoId: string;
  devolvida: number;
  retidas: RetidaComando[];
  /** O que a lavanderia contou de fato, só pro cálculo do excedente (ADR-0021). */
  conferida?: number;
  conferidaMotivo?: string;
}

export type ComandoDevolucao =
  | {
      tipo: "registrar";
      loteId: string;
      data: DataISO;
      hora?: string;
      linhas: LinhaDevolucaoComando[];
    }
  | { tipo: "registrarEmMassa"; loteIds: string[] };

export function executarComandoDevolucao(
  snapshot: Snapshot,
  cmd: ComandoDevolucao,
  ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "registrar":
      // "Fechar OS" do operador: confere a Retida digitada na Coleta (ADR-0029)
      return registrarDevolucao(
        snapshot,
        cmd.loteId,
        { data: cmd.data, ...(cmd.hora ? { hora: cmd.hora } : {}), linhas: cmd.linhas },
        { conferirRetencao: true, exigirHora: true, registradoEm: ctx.agora, hoje: ctx.hoje },
      );
    case "registrarEmMassa":
      // uma transição só; a tela reconstrói o resumo do diff do Snapshot
      // (mesmo padrão de lote.lancarEmMassa)
      return registrarDevolucoesEmMassa(snapshot, cmd.loteIds, ctx.hoje, ctx.agora).snapshot;
    default:
      return assertNunca(cmd);
  }
}

// --- Validação da forma do comando (fronteira HTTP) -------------------------
//
// Igual ao agregado Lote/OS: quando o comando chega pela rede, `req.body` é
// `unknown`. Aqui só se confere a FORMA; as regras de negócio (quantidades
// inteiras, tipo existe, saldo do Backlog…) continuam no domínio, que já
// devolve erro amigável. Helpers comuns: `./_forma.ts`.

function validarRetidas(valor: unknown): RetidaComando[] {
  return lista(valor, "retidas", (item, i) => {
    const o = comoObjeto(item, `Retida #${i + 1}`);
    return {
      motivo: typeof o.motivo === "string" ? o.motivo : "",
      quantidade: paraNumero(o.quantidade),
    };
  });
}

export function validarLinhas(valor: unknown): LinhaDevolucaoComando[] {
  return lista(valor, "linhas", (item, i) => {
    const o = comoObjeto(item, `Linha #${i + 1}`);
    // `conferida` é opcional de verdade: omitido vira `undefined` (sem
    // correção), nunca `NaN` — senão o domínio recusaria toda Devolução sem
    // Conferida como se fosse um número inválido.
    const temConferida = o.conferida != null && o.conferida !== "";
    return {
      tipoId: exigirTexto(o.tipoId, `linhas[${i}].tipoId`),
      devolvida: paraNumero(o.devolvida),
      retidas: validarRetidas(o.retidas),
      ...(temConferida ? { conferida: paraNumero(o.conferida) } : {}),
      conferidaMotivo: texto(o.conferidaMotivo),
    };
  });
}

export function validarComandoDevolucao(valor: unknown): ComandoDevolucao {
  const c = comoObjeto(valor, "Comando de devolução");
  switch (c.tipo) {
    case "registrar":
      return {
        tipo: "registrar",
        loteId: exigirTexto(c.loteId, "loteId"),
        // `texto` e não `exigirTexto`: data vazia ou malformada chega crua e
        // quem recusa é o domínio, com "Informe a data da devolução." (ADR-0013).
        data: texto(c.data),
        ...(typeof c.hora === "string" && c.hora ? { hora: c.hora } : {}),
        linhas: validarLinhas(c.linhas),
      };
    case "registrarEmMassa":
      return {
        tipo: "registrarEmMassa",
        loteIds: exigirLista(c.loteIds, "loteIds", (id, i) => exigirTexto(id, `loteIds[${i}]`)),
      };
    default:
      throw new Error(
        `Tipo de comando de devolução desconhecido: ${String(c.tipo)}.`,
      );
  }
}
